import hoverintent from 'hoverintent';
import { onViewChange, getCurrentView } from '@utils/breakpoints';
import {
  loadCart,
  getFirstFocusableElement,
  getLastFocusableElement,
  trapFocus,
} from '@utils/global-functions';
import {
  topNavLi,
  drawerWrapper,
  level4Item,
  level3Item,
  level3ItemContainer,
  level2ItemContainer,
  level2Item,
  footerLi,
  exposedNavLink,
} from '@utils/NavResources';

/**
 * Static (DY-independent) navigation module.
 * Reads nav data directly from the #navigation_fallback_data script tag
 * and builds all nav elements immediately on construction — no DY pub/sub
 * or nanoLoaded waiting required.
 */
export default class StaticNavigation {
  constructor() {
    this.navSection = document.querySelector('#shopify-section-navigation');
    this.mobileNav = document.querySelector(
      'content-drawer[data-label="mobile-nav"]'
    );
    this.topDrawer = document.querySelector('.top-drawer');
    this.navOverlay = document.querySelector('.navigation-overlay');
    this.closeBtn = this.mobileNav.querySelector('.overlay__modal--close');
    this.backBtn = this.mobileNav.querySelector('.overlay__modal--back');
    this.mobileNavContent = this.mobileNav.querySelector(
      '.overlay__modal--content'
    );
    this.navSection.setAttribute('data-open', true);
    this.lastScrollTop = 0;
    this.initialScrollListeners();
    this.initNav();
  }

  /**
   * Attaches scroll listeners immediately and sets the sticky data attribute
   * after a short delay for a smoother transition effect.
   */
  initialScrollListeners() {
    this.scrollListeners();
    setTimeout(() => {
      this.navSection.setAttribute('data-sticky', true);
    }, 800);
    onViewChange(() => this.scrollListeners(), 'med', 'max');
    document.addEventListener('scroll', () => {
      this.scrollListeners();
    });
  }

  /**
   * Reads nav data from the #navigation_fallback_data script tag, then calls
   * loadHeader() to build all navigation elements.
   */
  initNav() {
    window.Brooklinen.nav = JSON.parse(
      document.querySelector('#navigation_fallback_data').textContent
    );

    this.loadHeader();
    this.setNavOffset();
    window.addEventListener('resize', this.setNavOffset.bind(this));
    window.addEventListener('scroll', this.setNavOffset.bind(this));
  }

  setNavOffset() {
    const headerOffset = `${Math.max(
      0,
      this.navSection?.getBoundingClientRect().bottom ?? 0
    )}px`;
    document.documentElement.style.setProperty('--navOffset', headerOffset);
  }

  /**
   * Creates the desktop and mobile L1 links.
   * @param {Array} data - Top-nav entries from the nav JSON.
   */
  createTopnavElements(data) {
    const desktopList = document.querySelector(
      '.header__inline-menu > .list-menu'
    );
    const mobileList = this.mobileNav.querySelector('ul.level-1__item');
    for (const i in data) {
      const drawerId = data[i].label.replaceAll(' ', ''),
        isDrawerLink = window.Brooklinen.nav[drawerId];
      desktopList.innerHTML += topNavLi(drawerId, isDrawerLink, data[i]);
      mobileList.innerHTML += topNavLi(drawerId, isDrawerLink, data[i]);
    }
    return true;
  }

  /**
   * Creates the L2/L3/L4 items for desktop and mobile, as well as the
   * individual drawers that house these elements.
   * @param {string} key - The nav key (drawer ID).
   */
  async createDrawerElements(key) {
    await this.createTopnavElements();

    const drawerData = window.Brooklinen.nav[key];

    const drawerColumns = drawerData.reduce((a, b) =>
      a.columnOrder > b.columnOrder ? a : b
    )?.columnOrder;

    this.topDrawer.innerHTML += drawerWrapper(key, drawerColumns);
    const drawerContentContainer = this.topDrawer.querySelector(
      `.drawer-wrapper[data-wrapper="${key}"]`
    );
    this.mobileNav.querySelector('.mobile-nav--body').innerHTML +=
      drawerWrapper(key, drawerColumns);

    const mobileL1Link = this.mobileNav.querySelector(
      `.level-1__item a[data-drawer="${key}"]`
    );
    mobileL1Link.setAttribute('href', '#');
    const buttonEl = mobileL1Link.outerHTML;
    mobileL1Link.outerHTML = buttonEl
      .replace(
        /<a[^>]*>/g,
        `<button data-ht-element="nav-l1-link" class="header__menu-item list-menu__item link" data-drawer="${key}">`
      )
      .replace(/<\/a>/g, '</button>');
    const mobileContentContainer = this.mobileNav.querySelector(
      `.drawer-wrapper[data-wrapper="${key}"]`
    );

    if (!drawerContentContainer || !mobileContentContainer) {
      throw new Error(
        `[StaticNavigation] Missing drawer container for key: ${key}`
      );
    }

    for (let i = 1; i <= drawerColumns; i++) {
      const currentColumnData = drawerData.filter(
        (line) => line.columnOrder == i
      );

      const currentColumnType = currentColumnData?.[0].moduleType;

      switch (true) {
        case currentColumnType == 'L3': {
          const labelTitles = currentColumnData.filter((data) => !data.label);
          const l3Data = currentColumnData.filter((data) => data.label);

          for (const j in labelTitles) {
            const obj = {
              key: key,
              labelTitle: labelTitles[j].labelTitle,
              url: labelTitles[j].url,
            };
            drawerContentContainer.querySelector(
              `.drawer-wrapper__col-${i}`
            ).innerHTML += level3ItemContainer(obj);

            if (
              mobileContentContainer.querySelector(`.level-2__list`) &&
              labelTitles[j].mobileL3PositionOverride
            ) {
              if (!mobileContentContainer.querySelector('.pinned')) {
                const pinnedLocation = parseInt(
                  labelTitles[j].mobileL3PositionOverride + 1
                );
                const l2PinnedEl = mobileContentContainer.querySelector(
                  `.level-2__list .level-2__item:nth-child(${pinnedLocation})`
                );
                l2PinnedEl.classList.add('pinned');
              }
              mobileContentContainer
                .querySelector('.pinned')
                .insertAdjacentHTML('beforebegin', level3ItemContainer(obj));
            } else {
              mobileContentContainer.querySelector(
                `.drawer-wrapper__col-${i}`
              ).innerHTML += level3ItemContainer(obj);
            }
          }

          for (const j in l3Data) {
            const obj = {
              key: key,
              label: l3Data[j].label,
              url: l3Data[j].url,
              badge: l3Data[j].badge,
              caption: l3Data[j].caption,
            };
            const handleizedLabel = l3Data[j].labelTitle
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-');
            drawerContentContainer.querySelector(
              `.drawer-wrapper__col-${i} .list--${handleizedLabel}`
            ).innerHTML += level3Item(obj);
            mobileContentContainer.querySelector(
              `.list--${handleizedLabel}`
            ).innerHTML += level3Item(obj);
          }

          break;
        }
        case currentColumnType == 'L2':
          drawerContentContainer.querySelector(
            `.drawer-wrapper__col-${i}`
          ).innerHTML += level2ItemContainer(key);
          mobileContentContainer.querySelector(
            `.drawer-wrapper__col-${i}`
          ).innerHTML += level2ItemContainer(key);

          for (const j in currentColumnData) {
            const obj = {
              key: key,
              label: currentColumnData[j].label,
              url: currentColumnData[j].url,
              badge: currentColumnData[j].badge,
            };
            drawerContentContainer.querySelector(
              `.drawer-wrapper__col-${i} .level-2__list`
            ).innerHTML += level2Item(obj);

            mobileContentContainer.querySelector(
              `.drawer-wrapper__col-${i} .level-2__list`
            ).innerHTML += level2Item(obj);
          }

          break;
        case currentColumnType.includes('Tile'):
          for (const j in currentColumnData) {
            const obj = {
              key: key,
              url: currentColumnData[j].url,
              image: currentColumnData[j].tileImage,
              cta: currentColumnData[j].label,
              description: currentColumnData[j].caption,
              imageType: currentColumnType == 'SML Tile' ? 'sml' : 'lrg',
            };
            drawerContentContainer.querySelector(
              `.drawer-wrapper__col-${i}`
            ).innerHTML += level4Item(obj);

            mobileContentContainer.innerHTML += level4Item(obj);
          }
          break;
        default:
          break;
      }
    }
  }

  /**
   * Creates the login / misc links in the mobile drawer footer.
   * @param {Array} data - Footer nav entries from the nav JSON.
   */
  createMobileFooterElements(data) {
    for (const i in data) {
      this.mobileNav.querySelector(
        '.overlay__modal--content ul.mobile-nav--footer-links'
      ).innerHTML += footerLi(data[i].url, data[i].label);
    }
  }

  /**
   * Inserts the exposed nav bar below the header section (homepage only).
   * @param {Array} data - Exposed nav entries from the nav JSON.
   */
  createExposedNavElement(data) {
    if (data.length > 0) {
      const exposedNav = document.createElement('div');
      exposedNav.classList.add('exposed-nav');
      exposedNav.setAttribute('data-ht-module', 'exposed-nav');
      for (const i in data) {
        exposedNav.innerHTML += exposedNavLink(data[i].label, data[i].url, i);
      }
      this.navSection.after(exposedNav);
    }
  }

  /**
   * Iterates through the nav JSON and calls the appropriate build functions
   * for each key (topnav, footernav, exposednav, drawer keys).
   */
  buildElements() {
    if (window.Brooklinen.nav) {
      for (const i in Object.keys(window.Brooklinen.nav)) {
        switch (Object.keys(window.Brooklinen.nav)[i]) {
          case 'topnav':
            this.createTopnavElements(window.Brooklinen.nav.topnav);
            break;
          case 'footernav':
            this.createMobileFooterElements(window.Brooklinen.nav.footernav);
            break;
          case 'exposednav':
            if (document.querySelector('main[data-template="index"]')) {
              this.createExposedNavElement(window.Brooklinen.nav.exposednav);
            }
            break;
          default:
            this.createDrawerElements(Object.keys(window.Brooklinen.nav)[i]);
            break;
        }
        this.setTabNavBehavior(Object.keys(window.Brooklinen.nav)[i]);
      }
    }
    return true;
  }

  async buildHeaderStyles(drawerId) {
    const setStyleProperties = () => {
      const activeDrawer = this.topDrawer.querySelector(
        'div.active[data-wrapper]'
      );

      this.topDrawer.style.minHeight = this.topDrawer.offsetHeight + 'px';
      if (
        activeDrawer &&
        activeDrawer.getAttribute('data-wrapper') != drawerId
      ) {
        activeDrawer.style.opacity = '0';
        activeDrawer.classList.remove('active');
      }

      this.topDrawer
        .querySelector(`div[data-wrapper="${drawerId}"]`)
        ?.classList.add('active');
      this.topDrawer.style.minHeight =
        (this.topDrawer.querySelector('div.active[data-wrapper]')
          ?.offsetHeight ?? 0) + 'px';
      this.topDrawer.classList.add('active');

      return true;
    };

    setStyleProperties();
  }

  /**
   * Stores all event listeners for dynamically generated nav elements
   * (drawer hover, mobile L1 click, close/back buttons, etc.).
   */
  initBuiltElementListeners() {
    const linksWithoutDrawer = this.navSection.querySelectorAll(
      'static-nav li[data-drawer] a:not([data-drawer])'
    );

    linksWithoutDrawer.forEach((link) => {
      const closeActiveDrawer = () => {
        this.navOverlay.classList.remove('active');
        this.topDrawer.classList.remove('active');
      };

      hoverintent(link, closeActiveDrawer, () => {}).options({ interval: 250 });
    });

    const linksWithDrawer = this.navSection.querySelectorAll(
      'static-nav a[data-drawer]'
    );

    linksWithDrawer.forEach((link) => {
      const drawerId = link.getAttribute('data-drawer');

      const openDrawer = () => {
        const drawerContent = this.topDrawer.querySelector(
          `div[data-wrapper="${drawerId}"]`
        );

        this.navOverlay.classList.add('active');

        const value = this.buildHeaderStyles(drawerId);
        if (value) drawerContent.style.opacity = 1;
      };

      hoverintent(link, openDrawer, () => {}).options({ interval: 250 });
      link.addEventListener('focus', openDrawer);

      this.mobileNav
        .querySelector(`.level-1__item [data-drawer="${drawerId}"]`)
        ?.addEventListener('click', () => {
          const mainDrawer = this.mobileNav;
          mainDrawer.querySelector('.mobile-nav--l2-header').innerHTML =
            drawerId.replace(/([A-Z])/g, ' $1').trim();
          this.mobileNavContent.classList.add('level-2--active');
          const l2Drawer = mainDrawer.querySelector(
            `.drawer-wrapper[data-wrapper="${drawerId}"]`
          );
          this.closeBtn.disabled = true;
          this.backBtn.disabled = false;
          l2Drawer.classList.add('active');
          trapFocus(mainDrawer, l2Drawer);
        });
    });

    this.closeBtn.addEventListener('click', () => {
      this.closeMobileDrawer();
    });

    this.mobileNavContent.addEventListener('click', (e) => {
      const overlayBounds = document.documentElement.clientWidth - e.pageX;
      if (overlayBounds <= 40) this.closeMobileDrawer();
    });

    this.topDrawer.addEventListener('mouseleave', () => {
      [this.navOverlay, this.topDrawer].forEach((el) => {
        el.classList.remove('active');
      });
      this.topDrawer.style.minHeight = '';
    });

    this.backBtn.addEventListener('click', () => {
      this.goBack();
    });

    document
      .querySelectorAll('.mobile-nav--body [data-drawer-link]')
      .forEach((link) => {
        link.addEventListener('click', (e) => {
          window.location.href = e.target
            .querySelector('a')
            .getAttribute('href');
        });
      });

    const mobileNavHeader = this.mobileNav.querySelector('.mobile-nav--header');
    mobileNavHeader
      .querySelector('.header__heading-logo')
      ?.addEventListener('click', () => {
        this.closeMobileDrawer();
        setTimeout(() => {
          location.reload();
        }, 1000);
      });

    mobileNavHeader
      .querySelector('.header__icon--cart')
      ?.addEventListener('click', (e) => {
        e.stopImmediatePropagation();
        this.closeMobileDrawer();
        loadCart();
      });

    onViewChange(() => this.closeMobileDrawer(), '1100px', 'min');
    this.scrollListeners();
  }

  /**
   * Closes the mobile navigation drawer.
   */
  closeMobileDrawer() {
    const closeOverlayBackground = new CustomEvent('close-overlay', {
      detail: 'mobile-nav',
    });
    window.dispatchEvent(closeOverlayBackground);
  }

  /**
   * Moves the mobile drawer from L2 state back to L1.
   */
  goBack() {
    const mainDrawer = this.mobileNav;
    this.mobileNavContent.classList.remove('level-2--active');
    mainDrawer
      .querySelector('.drawer-wrapper.active')
      .classList.remove('active');
    this.closeBtn.disabled = false;
    this.backBtn.disabled = true;
    trapFocus(mainDrawer);
  }

  /**
   * Determines vertical scroll position and toggles sticky/visible state of
   * the header accordingly. Also handles the nanobar offset on scroll.
   * Uses optional chaining on nano-bar queries to support both the DY
   * nano-bar custom element and the static .nano--container fallback.
   */
  scrollListeners() {
    const mobileScrollThreshold = parseInt(
      document.querySelector('header').getAttribute('data-scroll-threshold') ||
        100
    );

    if (getCurrentView() == 'sml' || getCurrentView() == 'med') {
      const st = window.scrollY || document.documentElement.scrollTop;
      if (st > this.lastScrollTop) {
        if (
          (document.body.scrollTop > 0 ||
            document.documentElement.scrollTop > 0) &&
          st < mobileScrollThreshold
        ) {
          // Nano is a separate section above nav; keep nav pinned at top when sticky
          this.navSection.style.top = '0';
          document
            .querySelector('.js-nanobar.active')
            ?.style.setProperty('margin-top', '-1px');
        } else {
          this.navSection.style.top = `-${this.navSection.offsetHeight}px`;
          document
            .querySelector('.js-nanobar.active')
            ?.style.setProperty('margin-top', '0');
        }

        document
          .querySelector('.section-in-page-navigation')
          ?.setAttribute('data-scroll-direction', 'down');
      } else if (st < this.lastScrollTop) {
        if (!document.body.getAttribute('data-scrollable') !== false) {
          // Nano is a separate section above nav; keep nav pinned at top when sticky
          this.navSection.style.top = '0';
          document
            .querySelector('.section-in-page-navigation')
            ?.setAttribute('data-scroll-direction', 'up');
        }
      }
      this.lastScrollTop = st <= 0 ? 0 : st;
    } else {
      if (
        document.body.scrollTop > 0 ||
        document.documentElement.scrollTop > 0
      ) {
        // Nano is a separate section above nav; keep nav pinned at top when sticky
        this.navSection.style.top = '0';
        document
          .querySelector('.js-nanobar.active')
          ?.style.setProperty('margin-top', '-1px');
      } else {
        this.navSection.style.top = '0';
        document
          .querySelector('.js-nanobar.active')
          ?.style.setProperty('margin-top', '0');
      }
    }
  }

  /**
   * Checks if the user is on an L1 page and adds active state. Desktop only.
   */
  checkActiveL1() {
    document
      .querySelectorAll('header [data-ht-element="nav-l1-link"]')
      .forEach((L1) => {
        const navHref = L1.getAttribute('href');
        if (window.location.href.includes(navHref) && navHref != '#') {
          L1.classList.add('current--page');
        }
      });
  }

  setTabNavBehavior(navType = 'drawer') {
    switch (navType) {
      case 'topnav': {
        const l1s = document.querySelectorAll(
          '[data-ht-element="nav-l1-link"]'
        );
        l1s.forEach((l1, i) => {
          l1.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              const topDrawerList = document.querySelector(
                '.top-drawer.active div.active'
              );
              if (topDrawerList) {
                e.preventDefault();
                const first = getFirstFocusableElement(topDrawerList);
                const last = getLastFocusableElement(topDrawerList);
                first.focus();

                last.addEventListener('keydown', (e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault;
                    const nextL1 = l1s[i];
                    if (nextL1) {
                      nextL1.focus();
                    }
                  }
                });
              }
            }
          });
        });

        const navIcons = document.querySelectorAll('.header__icons button');
        const firstIcon = navIcons[1];
        const lastIcon = navIcons[navIcons.length - 1];
        const firstMainFocusable = getFirstFocusableElement(
          document.querySelector('main')
        );
        const exposedNav = document.querySelector('.exposed-nav');
        const exposedNavLink = exposedNav
          ? getFirstFocusableElement(exposedNav)
          : null;

        if (firstIcon) {
          firstIcon.addEventListener('focus', () => {
            this.topDrawer.classList.remove('active');
            this.navOverlay.classList.remove('active');
          });
        }

        if (lastIcon) {
          lastIcon.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              if (!exposedNavLink || exposedNav.style.display === 'none') {
                firstMainFocusable.focus();
              } else {
                exposedNavLink.focus();
              }
            }
          });
        }
      }
    }
  }

  /**
   * Builds all nav elements, attaches event listeners, marks active L1,
   * and flags navLoaded on window.Brooklinen.
   */
  async loadHeader() {
    await this.buildElements();
    this.initBuiltElementListeners();
    this.checkActiveL1();
    window.Brooklinen.navLoaded = true;
  }
}
